/**
 * UserContext.js - Centralized authentication state management
 * This context provides a single source of truth for user authentication state
 * to prevent redundant API calls when multiple components need user information.
 */
import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getAuthenticatedUser } from "../utils/rbac";

// Create context to store and provide user data across the application
const UserContext = createContext(null);

/**
 * UserProvider - Authentication context provider component
 * Manages user authentication state and provides it to all child components
 * Eliminates redundant API calls by centralizing auth state in one place
 */
export const UserProvider = ({ children }) => {
  // Get Auth0 authentication state
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
  } = useAuth0();

  // Internal state for database user
  const [dbUser, setDbUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Effect to fetch user data from database when Auth0 authentication completes
  useEffect(() => {
    const fetchUser = async () => {
      if (!isAuthenticated || !auth0User) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Get access token from Auth0
        const accessToken = await getAccessTokenSilently({
          detailedResponse: true,
        });

        // Fetch user data from our database using Auth0 credentials
        const userData = await getAuthenticatedUser(
          auth0User,
          accessToken.access_token || accessToken
        );

        setDbUser(userData);
        setError(null);
      } catch (err) {
        console.error("Error fetching authenticated user from database:", err);

        // Check for refresh token errors and handle specifically
        if (
          err.message &&
          (err.message.includes("refresh token") ||
            err.message.includes("invalid_grant") ||
            err.message.includes("access denied"))
        ) {
          setError("Authentication session expired. Please log in again.");
        } else {
          setError(err.message || "Failed to fetch user data from database");
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch user data when Auth0 loading is complete
    if (!isAuth0Loading) {
      fetchUser();
    }
  }, [auth0User, isAuthenticated, isAuth0Loading, getAccessTokenSilently]);

  // Helper functions for common user state checks
  const hasRole = (role) => dbUser && dbUser.role === role;
  const isDbAdmin = () => hasRole("admin");
  const isInvited = () => dbUser && dbUser.invited === true;

  // Prepare the context value with all user state and helper functions
  const value = {
    dbUser,
    isLoading: isLoading || isAuth0Loading,
    isDbUserLoading: isLoading || isAuth0Loading,
    error,
    hasRole,
    isDbAdmin,
    isInvited,
  };

  // Provide the user context to all child components
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * useDbUser - Hook to access the user context
 * This hook provides components with access to the centralized user state
 * @returns {Object} User context containing dbUser, loading states, error, and helper functions
 */
export const useDbUser = () => useContext(UserContext);
